import {
  PAYMENT_METHOD_LABELS,
  formatDateShort,
  formatDateTime,
  formatDuration,
  formatMoney,
  navigationUrl,
  telHref,
  type VisitDetailResponse,
  type VisitDto,
} from '@kominiarz/shared';
import {
  Ban,
  Banknote,
  Bell,
  CalendarCheck,
  CalendarClock,
  CalendarPlus,
  Check,
  ClipboardPen,
  Copy,
  Eye,
  FileText,
  HardHat,
  MapPin,
  Navigation,
  NotebookPen,
  Pencil,
  Phone,
  RotateCcw,
  Trash2,
  UserRound,
  Wallet,
} from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useCurrentUser } from '../../auth/useAuth';
import { Alert } from '../../components/Alert';
import { ActionTile, ActionTiles } from '../../components/ActionTiles';
import { ColorDot } from '../../components/ColorDot';
import { useConfirm } from '../../components/Confirm';
import { Spinner } from '../../components/Loading';
import { ActionRow, InfoRow, List } from '../../components/List';
import { MarkersMap } from '../../components/map';
import { PinControl } from '../../components/PinControl';
import { PageBody } from '../../components/PageBody';
import { PageHeader } from '../../components/PageHeader';
import { Section } from '../../components/Section';
import { useToast } from '../../components/Toast';
import { CloseVisitDialog } from '../../components/visit/CloseVisitDialog';
import { PaymentDialog } from '../../components/visit/PaymentDialog';
import { PhotoGallery } from '../../components/visit/PhotoGallery';
import { PhotoPicker } from '../../components/visit/PhotoPicker';
import {
  UnpaidBadge,
  VisitStatusBadge,
  visitClientName,
  visitColor,
  visitTimeRange,
  visitTitle,
} from '../../components/VisitStatus';
import { errorMessage } from '../../lib/api';
import { capitalize, local, timeRange } from '../../lib/calendarDates';
import { useUserOptions } from '../../lib/usersApi';
import { useCloseVisit, useDeleteVisit, useUpdateVisit, useVisit } from '../../lib/visitsApi';
import { AuditList } from './AuditList';
import styles from './visits.module.css';

export function VisitDetailPage() {
  const id = Number(useParams().id);
  const query = useVisit(Number.isFinite(id) ? id : null);

  if (query.isPending) {
    return (
      <>
        <PageHeader title="Wizyta" back="/kalendarz" />
        <PageBody>
          <Spinner />
        </PageBody>
      </>
    );
  }
  if (query.isError) {
    return (
      <>
        <PageHeader title="Wizyta" back="/kalendarz" />
        <PageBody>
          <Alert kind="error">{errorMessage(query.error)}</Alert>
        </PageBody>
      </>
    );
  }
  return <VisitDetail detail={query.data} />;
}

function VisitDetail({ detail }: { detail: VisitDetailResponse }) {
  const { visit: v, audit, photos } = detail;
  const user = useCurrentUser();
  const users = useUserOptions();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const toast = useToast();
  const close = useCloseVisit();
  const remove = useDeleteVisit();
  const update = useUpdateVisit();
  const [dialog, setDialog] = useState<'close' | 'edit' | 'payment' | null>(null);
  const coords = v.lat != null && v.lng != null ? { lat: v.lat, lng: v.lng } : null;
  const unpaid = v.status === 'done' && v.paymentStatus === 'unpaid' && Boolean(v.amountGrosze);

  async function savePin(p: { lat: number; lng: number }) {
    try {
      await update.mutateAsync({ id: v.id, body: { lat: p.lat, lng: p.lng } });
      toast.success('Zapisano położenie');
    } catch (err) {
      toast.error(errorMessage(err));
    }
  }

  const start = local(v.startAt);
  const navUrl = navigationUrl(user.settings.navApp, v);
  const client = visitClientName(v);
  const sharedNames = v.sharedWith.map((id) => users.data?.find((u) => u.id === id)?.name ?? 'osoba bez konta');

  async function setStatus(status: VisitDto['status'], message: string) {
    try {
      await close.mutateAsync({ id: v.id, body: { status } });
      toast.success(message);
    } catch (err) {
      toast.error(errorMessage(err));
    }
  }

  async function cancelVisit() {
    const ok = await confirm({
      title: 'Odwołać wizytę?',
      message: `${visitTitle(v)}, ${start.toFormat('d MMMM, HH:mm')}. Wizyta zostanie w kalendarzu jako odwołana.`,
      confirmLabel: 'Odwołaj wizytę',
      cancelLabel: 'Nie odwołuj',
      danger: true,
    });
    if (ok) await setStatus('cancelled', 'Wizyta odwołana');
  }

  async function deleteVisit() {
    const ok = await confirm({
      title: 'Usunąć wizytę?',
      message: `${visitTitle(v)}, ${start.toFormat('d MMMM, HH:mm')}. Wizyta zniknie z kalendarza. Jeśli wizyta się nie odbędzie, lepiej ją odwołać.`,
      confirmLabel: 'Usuń wizytę',
      cancelLabel: 'Nie usuwaj',
      danger: true,
    });
    if (!ok) return;
    try {
      await remove.mutateAsync(v.id);
      toast.success('Wizyta usunięta');
      navigate('/kalendarz', { replace: true });
    } catch (err) {
      toast.error(errorMessage(err));
    }
  }

  return (
    <>
      <PageHeader
        title={visitTitle(v)}
        subtitle={`${start.toFormat('EEEE, d MMMM')}, ${v.arrivalWindowMinutes ? `przyjazd ${visitTimeRange(v)}` : timeRange(v.startAt, v.endAt)}`}
        back="/kalendarz"
      />
      <PageBody>
        {v.status !== 'planned' && (
          <div className={styles.badges}>
            <VisitStatusBadge status={v.status} />
            <UnpaidBadge visit={v} />
          </div>
        )}

        <div className={styles.columns}>
          <div className={styles.column}>
            <ActionTiles>
              {v.phone && <ActionTile href={telHref(v.phone)} icon={<Phone aria-hidden="true" />} label="Zadzwoń" />}
              {navUrl && (
                <ActionTile
                  href={navUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  icon={<Navigation aria-hidden="true" />}
                  label="Nawiguj"
                />
              )}
              {v.permissions.close && v.status === 'planned' && (
                <ActionTile primary icon={<Check aria-hidden="true" />} label="Oznacz jako wykonaną" onClick={() => setDialog('close')} />
              )}
              {v.permissions.close && unpaid && (
                <ActionTile primary icon={<Banknote aria-hidden="true" />} label="Oznacz jako opłaconą" onClick={() => setDialog('payment')} />
              )}
            </ActionTiles>

            <Section title="Szczegóły" flush>
              <List>
                <InfoRow icon={CalendarClock} label="Termin">
                  {capitalize(start.toFormat('EEEE, d MMMM yyyy'))}
                  {'\n'}
                  {v.arrivalWindowMinutes
                    ? `Przyjazd między ${visitTimeRange(v).replace('–', ' a ')}, potem ${formatDuration(v.durationMinutes)} pracy`
                    : `${timeRange(v.startAt, v.endAt)} (${formatDuration(v.durationMinutes)})`}
                </InfoRow>
                {client && (
                  <InfoRow icon={UserRound} label="Klient">
                    {v.client && !v.client.anonymized ? <Link to={`/klienci/${v.client.id}`}>{client}</Link> : client}
                  </InfoRow>
                )}
                {v.address && (
                  <InfoRow icon={MapPin} label="Adres">
                    {v.object?.label ? `${v.object.label}: ` : ''}
                    {v.address}
                  </InfoRow>
                )}
                {v.phone && (
                  <InfoRow icon={Phone} label="Telefon">
                    <a href={telHref(v.phone)}>{v.phone}</a>
                  </InfoRow>
                )}
                {v.description && (
                  <InfoRow icon={FileText} label="Opis">
                    {v.description}
                  </InfoRow>
                )}
                {v.amountGrosze != null && (
                  <InfoRow icon={Banknote} label="Kwota">
                    {formatMoney(v.amountGrosze)}
                  </InfoRow>
                )}
                <InfoRow icon={HardHat} label={v.assignees.length > 1 ? 'Wykonawcy' : 'Wykonawca'}>
                  <span className={styles.people}>
                    {v.assignees.map((u) => (
                      <span key={u.id} className={styles.person}>
                        <ColorDot color={u.color} /> {u.name}
                      </span>
                    ))}
                  </span>
                </InfoRow>
                {v.reminderMinutes !== null && (
                  <InfoRow icon={Bell} label="Przypomnienie">
                    {v.reminderMinutes === 0 ? 'Bez przypomnienia' : `${formatDuration(v.reminderMinutes)} przed wizytą`}
                  </InfoRow>
                )}
                <InfoRow icon={Eye} label="Kto widzi">
                  {v.visibility === 'selected' && sharedNames.length > 0
                    ? `Autor, wykonawcy, kierownik oraz: ${sharedNames.join(', ')}`
                    : 'Autor, wykonawcy i kierownik'}
                </InfoRow>
              </List>
            </Section>
            <p className={styles.meta}>
              Dodano: {formatDateTime(v.createdAt)} ({v.createdBy.name})
              {v.updatedBy && v.updatedAt !== v.createdAt && (
                <>
                  <br />
                  Ostatnia zmiana: {formatDateTime(v.updatedAt)} ({v.updatedBy.name})
                </>
              )}
            </p>
          </div>

          <div className={styles.column}>
            {(v.status === 'done' || photos.length > 0 || v.afterNote) && (
              <Section title="Po wizycie" flush>
                <List>
                  <InfoRow icon={NotebookPen} label="Notatka">
                    {v.afterNote || <span className={styles.none}>Brak notatki</span>}
                  </InfoRow>
                  <InfoRow icon={Wallet} label="Płatność">
                    {v.paymentStatus === 'paid'
                      ? [
                          'Opłacona',
                          v.paymentMethod && PAYMENT_METHOD_LABELS[v.paymentMethod].toLowerCase(),
                          v.paidAt && formatDateShort(v.paidAt),
                        ]
                          .filter(Boolean)
                          .join(', ')
                      : v.amountGrosze
                        ? `Nieopłacona (${formatMoney(v.amountGrosze)})`
                        : 'Nieopłacona'}
                  </InfoRow>
                  {detail.nextInspectionDue && (
                    <InfoRow icon={CalendarCheck} label="Kolejny przegląd">
                      {capitalize(formatDateShort(detail.nextInspectionDue))}
                    </InfoRow>
                  )}
                </List>
                {(photos.length > 0 || v.permissions.close) && (
                  <div className={styles.photos}>
                    <PhotoGallery photos={photos} />
                    {v.permissions.close && <PhotoPicker visitId={v.id} />}
                  </div>
                )}
                {v.permissions.close && v.status === 'done' && (
                  <List divided>
                    <ActionRow icon={ClipboardPen} onClick={() => setDialog('edit')}>
                      Zmień dane po wizycie
                    </ActionRow>
                  </List>
                )}
              </Section>
            )}

            <Section title="Inne działania" flush>
              <List>
                {v.permissions.edit && (
                  <ActionRow icon={Pencil} to={`/wizyty/${v.id}/edycja`}>
                    Edytuj wizytę
                  </ActionRow>
                )}
                <ActionRow icon={Copy} to={`/wizyty/nowa?z=${v.id}`}>
                  Duplikuj
                </ActionRow>
                <ActionRow icon={CalendarPlus} to={`/wizyty/nowa?z=${v.id}&zaRok=1`}>
                  Umów ponownie za rok
                </ActionRow>
                {v.permissions.close && v.status !== 'planned' && (
                  <ActionRow icon={RotateCcw} onClick={() => setStatus('planned', 'Wizyta znów jest zaplanowana')}>
                    Przywróć jako zaplanowaną
                  </ActionRow>
                )}
                {v.permissions.close && v.status === 'planned' && (
                  <ActionRow icon={Ban} onClick={cancelVisit}>
                    Odwołaj wizytę
                  </ActionRow>
                )}
                {v.permissions.delete && (
                  <ActionRow icon={Trash2} tone="danger" onClick={deleteVisit}>
                    Usuń wizytę
                  </ActionRow>
                )}
              </List>
            </Section>
            {!v.permissions.edit && v.permissions.close && (
              <p className={styles.meta}>
                Wizytę dodała inna osoba, więc nie zmienisz jej terminu ani danych. Możesz ją oznaczyć jako wykonaną albo
                odwołać.
              </p>
            )}
          </div>
        </div>

        {(coords || (v.address && v.permissions.edit)) && (
          <Section title="Na mapie">
            {coords && (
              <MarkersMap
                showBase={false}
                height="16rem"
                markers={[{ id: v.id, lat: coords.lat, lng: coords.lng, color: visitColor(v), title: v.address ?? visitTitle(v) }]}
              />
            )}
            {v.permissions.edit && v.address && <PinControl address={v.address} coords={coords} onPin={savePin} />}
          </Section>
        )}

        {audit && audit.length > 0 && (
          <Section title="Historia zmian">
            <AuditList entries={audit} />
          </Section>
        )}
      </PageBody>
      {(dialog === 'close' || dialog === 'edit') && <CloseVisitDialog detail={detail} mode={dialog} onClose={() => setDialog(null)} />}
      {dialog === 'payment' && <PaymentDialog visit={v} onClose={() => setDialog(null)} />}
    </>
  );
}
